import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Grid,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  TextField,
  Typography,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { paddingCardsTables } from 'commons/values';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import Pagination from '@mui/material/Pagination';

import { useTableState } from 'hooks/useTableState';
import { getAllAppReleasesStore, deleteAppReleaseStore, restoreAppReleaseStore } from 'store/app-releases';
import { StyledTableCell, StyledTableRow } from 'commons/generics/StyledTableComponents';
import { GenericTableHead } from 'commons/generics/GenericTableHead';
import { SinDatos } from 'commons/generics/SinDatos';
import { corregirTexto, firstLetterUpperCase, hasPemissions, usarFuncionesComposicion, formatDateToYmd } from 'commons/utils';
import { ModalConfirmDeactivation } from 'components/modals/ModalConfirmDeactivation';
import { ModalConfirmRestore } from 'components/modals/ModalConfirmRestore';
import { ClampedText } from 'commons/generics/ClampedText';
import { StatusChip } from 'commons/generics/StatusChip';

const headCells = [
  { id: 'version', label: 'Versión' },
  { id: 'application', label: 'Aplicación' },
  { id: 'releasedAt', label: 'Lanzamiento' },
  { id: 'features', label: 'Novedades' },
  { id: 'status', label: 'Estado' }, 
  { id: 'actions', label: 'Acciones' },
];

export const TableAppReleases = () => {
 
  const navigate = useNavigate();

 
  
  const { app_releases_list } = useSelector((state: any) => state.appReleases);
  const dispatch = useDispatch<any>();

  const {
    dataTableList, 
    checked,
    searchTerm, setSearchTerm,
    handleSwitchChange,
    handleSearch: handleSearchHook,
    handleResetSearch: handleResetSearchHook,
    page, rowsPerPage,
    handleChangePage, handleChangeRowsPerPage,
    pagePagination, itemCountPagination,
    changePaginationPage: changePaginationPageHook,
    openModalDeactivation, setOpenModalDeactivation,
    openModalRestore, setOpenModalRestore,
    openElem, setOpenElem,
    ResponseComponents,
    handleResponse,
    updatePaginationFromList,
    getSortedAndFilteredList,
    isServerSide,
    order,
    orderBy,
    handleRequestSort
  } = useTableState('appReleases');

  const [hasPermission, setHasPermission] = useState(true);
  const [canView, setCanView] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [canCreate, setCanCreate] = useState(false);
  const [canRestore, setCanRestore] = useState(false);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    updatePaginationFromList(app_releases_list);
  }, [app_releases_list, updatePaginationFromList]);

  const checkPermissions = async () => {
    const view = await hasPemissions('modulo.manager', 'modulo.manager.novedades', 'view');
    if (!view) setHasPermission(false);
    else setCanView(true);

    const edit = await hasPemissions('modulo.manager', 'modulo.manager.novedades', 'edit');
    if (edit) setCanEdit(true);

    const del = await hasPemissions('modulo.manager', 'modulo.manager.novedades', 'delete');
    if (del) setCanDelete(true);

    const restore = await hasPemissions('modulo.manager', 'modulo.manager.novedades', 'restore');
    if (restore) setCanRestore(true);

    const create = await hasPemissions('modulo.manager', 'modulo.manager.novedades', 'create');
    if (create) setCanCreate(true);
  };

  useEffect(() => {
    checkPermissions();
  }, []);

  const getInitialData = async (statusVal = checked ? 0 : 1) => {
    const response = await dispatch(getAllAppReleasesStore({ page: 1, take: rowsPerPage, status: statusVal, param: '' }));
    await handleResponse(response, 'listar novedades', false);
  };

  useEffect(() => {
    if (canView) {
      getInitialData();
    }
  }, [canView]);


  const handleView = (row: any) => {
       navigate(`/manager/novedades/${row.id}/detalle`);
  }
  const handleEdit = (row: any) => {
       navigate(`/manager/novedades/${row.id}/editar`);
  }



  const handleSearch = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const param = e.target.value;
    setSearchTerm(param);
    setInputValue(param);
    handleSearchHook(param, dispatch, (p: string) => getAllAppReleasesStore({ page: 1, take: rowsPerPage, status: checked ? 0 : 1, param: p }), getInitialData, null, 'buscar novedades');
  };

  const onSwitchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    handleSwitchChange(e);
    const newChecked = e.target.checked;
    const status = newChecked ? 0 : 1;
    
    if (searchTerm) {
        handleSearchHook(searchTerm, dispatch, (p: string) => getAllAppReleasesStore({ page: 1, take: rowsPerPage, status, param: p }), () => getInitialData(status), null, 'buscar novedades');
    } else {
        getInitialData(status);
    }
  };

  const onResetSearch = () => {
    setInputValue('');
    handleResetSearchHook(() => getInitialData());
  };

  const changePaginationPage = async (event: React.ChangeEvent<unknown>, value: number) => {
    const validPage = value < 1 ? 1 : value;
    changePaginationPageHook(event, validPage);
    const response = await dispatch(getAllAppReleasesStore({ page: validPage, take: rowsPerPage, status: checked ? 0 : 1, param: searchTerm }));
    await handleResponse(response, 'listar novedades', false);
  };

  const confirmDeactivate = async () => {
    if (!openElem) return;
    const response = await dispatch(deleteAppReleaseStore(openElem.id, { page: pagePagination, take: rowsPerPage, status: checked ? 0 : 1, param: searchTerm }));
    await handleResponse(response, 'desactivar novedad', true);
    setOpenModalDeactivation(false);
  };

  const confirmRestore = async () => {
    if (!openElem) return;
    const response = await dispatch(restoreAppReleaseStore(openElem.id, { page: pagePagination, take: rowsPerPage, status: checked ? 0 : 1, param: searchTerm }));
    await handleResponse(response, 'restaurar novedad', true);
    setOpenModalRestore(false);
  };

  if (!hasPermission) {
    return (
      <Box sx={{ p: 3, mt: 5 }}>
        <Typography variant="h6" color="error">
          No tienes permisos para ver este contenido.
        </Typography>
      </Box>
    );
  }

  const sortedList = getSortedAndFilteredList(
    dataTableList || [],
    ['version'],
    'status',
    checked ? 0 : 1
  );

  return (
    <Box sx={{ width: '100%', mt: paddingCardsTables }}>
      {ResponseComponents}
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <TextField
          placeholder="Buscar versión..."
          variant="outlined"
          size="small"
          autoComplete="off"
          value={inputValue}
          onChange={handleSearch}
          sx={{ width: 400 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
            endAdornment: inputValue ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={onResetSearch}><ClearIcon /></IconButton>
              </InputAdornment>
            ) : null
          }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Switch checked={checked} onChange={onSwitchChange} color="primary" />
          <Typography variant="body2" sx={{ color: '#4B5563', fontWeight: 500 }}>
            Mostrar solo inactivas
          </Typography>
        </Box>
      </Box>

      <Card sx={{ borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: 'none', overflow: 'hidden' }}>
        <TableContainer>
        <Table>
          <GenericTableHead 
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              headCells={headCells}
          />
          <TableBody>
            {sortedList.length === 0 ? (
              <StyledTableRow>
                <StyledTableCell colSpan={6}>
                  <SinDatos />
                </StyledTableCell>
              </StyledTableRow>
            ) : (
              sortedList
                .slice(isServerSide ? 0 : page * rowsPerPage, isServerSide ? rowsPerPage : page * rowsPerPage + rowsPerPage)
                .map((row: any) => (
                  <StyledTableRow key={row.id}>
                    <StyledTableCell>
                      {usarFuncionesComposicion(corregirTexto, firstLetterUpperCase, row.version)}
                    </StyledTableCell>
                    <StyledTableCell>
                      <ClampedText text={usarFuncionesComposicion(corregirTexto, firstLetterUpperCase, row.application)} />
                    </StyledTableCell>
                    <StyledTableCell>
                      {formatDateToYmd(row.releasedAt)}
                    </StyledTableCell>
                    <StyledTableCell>
                      {row.features}
                    </StyledTableCell>
                    <StyledTableCell>
                       <StatusChip status={row.status} />
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                        <span style={{ cursor: !canView ? 'not-allowed' : 'pointer' }}>
                          <IconButton size="small" onClick={() => canView && handleView(row)} disabled={!canView} color="info">
                            <VisibilityOutlinedIcon fontSize="small" />
                          </IconButton>
                        </span>
                        
                        <span style={{ cursor: !canEdit || row.status !== 1 ? 'not-allowed' : 'pointer' }}>
                          <IconButton size="small" onClick={() => canEdit && row.status === 1 && handleEdit(row)} disabled={!canEdit || row.status !== 1} color="primary">
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </span>
                        
                        {row.status === 1 ? (
                          <span style={{ cursor: !canDelete ? 'not-allowed' : 'pointer' }}>
                            <IconButton size="small" onClick={() => { if(canDelete) { setOpenElem(row); setOpenModalDeactivation(true); } }} disabled={!canDelete} color="error">
                              <DeleteOutlineOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        ) : (
                          <span style={{ cursor: !canRestore ? 'not-allowed' : 'pointer' }}>
                            <IconButton size="small" onClick={() => { if(canRestore) { setOpenElem(row); setOpenModalRestore(true); } }} disabled={!canRestore} color="success">
                              <RestoreOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        )}
                      </Box>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
        {sortedList.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={isServerSide ? itemCountPagination : sortedList.length}
            rowsPerPage={rowsPerPage}
            page={isServerSide ? 0 : page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Filas por página"
          />
        )}
        
        {sortedList.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, padding: 2.5, borderTop: 1, borderColor: 'divider' }}>
            <Pagination
              sx={{ justifyContent: 'center' }}
              color="primary"
              count={isServerSide ? Math.ceil(itemCountPagination / rowsPerPage) : Math.ceil(sortedList.length / rowsPerPage)}
              page={pagePagination}
              onChange={changePaginationPage}
              showFirstButton
              showLastButton
              shape="circular"
            />
          </Box>
        )}
      
      <ModalConfirmDeactivation
          open={openModalDeactivation}
          setOpen={setOpenModalDeactivation}
          title='¿Estás seguro que deseas desactivar esta novedad?'
          description='Esta novedad pasará a estado inactivo.'
          onConfirm={confirmDeactivate}
      />
      <ModalConfirmRestore
          open={openModalRestore}
          setOpen={setOpenModalRestore}
          title='¿Estás seguro que deseas restaurar esta novedad?'
          description='Esta novedad pasará a estado activo.'
          onConfirm={confirmRestore}
      />
    </Card>
    </Box>
  );
};
